import { useEffect, useState } from "react";
import axios from "axios";
import { FileDown, X, Check, Loader2, BookOpen, Send } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useReveal } from "../hooks/useReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATALOG_IMG =
    "https://images.unsplash.com/photo-1620203853151-496c7228306c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function CatalogModal({ onClose }) {
    const { t } = useLang();
    const c = t.catalog;
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        language: "fr",
    });
    const [state, setState] = useState({
        loading: false,
        success: false,
        error: "",
    });

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    const handle = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.language) {
            setState((s) => ({ ...s, error: c.requiredError }));
            return;
        }
        setState({ loading: true, success: false, error: "" });
        try {
            await axios.post(`${API}/catalog-request`, {
                ...form,
                catalog: "reliability",
            });
            setState({ loading: false, success: true, error: "" });
        } catch (err) {
            setState({
                loading: false,
                success: false,
                error: c.errorMsg,
            });
        }
    };

    const langOptions = [
        { value: "fr", label: c.fields.languageFr },
        { value: "en", label: c.fields.languageEn },
        { value: "both", label: c.fields.languageBoth },
    ];

    return (
        <div
            data-testid="catalog-modal"
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/70 backdrop-blur-sm animate-fade-up"
            onClick={onClose}
        >
            <div
                className="relative bg-white border-2 border-ink max-w-xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    data-testid="catalog-modal-close"
                    onClick={onClose}
                    className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center hover:bg-paper-alt transition-colors"
                    aria-label="close"
                >
                    <X size={20} />
                </button>

                <div className="p-6 md:p-10">
                    {state.success ? (
                        <div data-testid="catalog-success" className="text-center py-6">
                            <span className="inline-flex w-16 h-16 bg-mint items-center justify-center mb-5">
                                <Check size={30} className="text-ink" />
                            </span>
                            <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-ink leading-tight">
                                {c.successTitle}
                            </h3>
                            <p className="mt-4 text-ink-soft leading-relaxed">
                                {c.successMsg}
                            </p>
                            <button
                                data-testid="catalog-success-close"
                                onClick={onClose}
                                className="mt-8 btn-primary"
                            >
                                {c.successClose}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-10 h-10 bg-mint text-ink flex items-center justify-center">
                                    <BookOpen size={20} />
                                </span>
                                <span className="font-display text-xs uppercase tracking-[0.3em] text-ink-muted">
                                    {c.badge}
                                </span>
                            </div>
                            <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-ink leading-tight">
                                {c.modalTitle}
                            </h3>
                            <p className="mt-3 text-sm md:text-base text-ink-soft">
                                {c.modalSubtitle}
                            </p>

                            <form
                                onSubmit={submit}
                                data-testid="catalog-form"
                                className="mt-6 space-y-4"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="label">
                                            {c.fields.name} *
                                        </label>
                                        <input
                                            data-testid="catalog-name"
                                            name="name"
                                            value={form.name}
                                            onChange={handle}
                                            className="field"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label">
                                            {c.fields.company}
                                        </label>
                                        <input
                                            data-testid="catalog-company"
                                            name="company"
                                            value={form.company}
                                            onChange={handle}
                                            className="field"
                                        />
                                    </div>
                                    <div>
                                        <label className="label">
                                            {c.fields.email} *
                                        </label>
                                        <input
                                            data-testid="catalog-email"
                                            type="email"
                                            name="email"
                                            value={form.email}
                                            onChange={handle}
                                            className="field"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="label">
                                            {c.fields.phone} *
                                        </label>
                                        <input
                                            data-testid="catalog-phone"
                                            type="tel"
                                            name="phone"
                                            value={form.phone}
                                            onChange={handle}
                                            className="field"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Language version radio */}
                                <div>
                                    <label className="label">
                                        {c.fields.language} *
                                    </label>
                                    <div
                                        role="radiogroup"
                                        className="grid grid-cols-1 sm:grid-cols-3 gap-2"
                                    >
                                        {langOptions.map((opt) => (
                                            <label
                                                key={opt.value}
                                                data-testid={`catalog-lang-${opt.value}`}
                                                className={`cursor-pointer border px-4 py-3 flex items-center gap-2 text-sm font-display uppercase tracking-widest transition-colors ${
                                                    form.language === opt.value
                                                        ? "border-ink bg-ink text-white"
                                                        : "border-border text-ink hover:border-ink"
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="language"
                                                    value={opt.value}
                                                    checked={form.language === opt.value}
                                                    onChange={handle}
                                                    className="sr-only"
                                                />
                                                <span
                                                    className={`w-3 h-3 border ${
                                                        form.language === opt.value
                                                            ? "border-mint bg-mint"
                                                            : "border-ink"
                                                    }`}
                                                />
                                                <span>{opt.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {state.error && (
                                    <div
                                        data-testid="catalog-error"
                                        className="px-4 py-3 border border-destructive text-destructive text-sm"
                                    >
                                        {state.error}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-3 pt-2">
                                    <button
                                        type="button"
                                        data-testid="catalog-cancel"
                                        onClick={onClose}
                                        className="px-5 py-3 font-display text-xs uppercase tracking-widest text-ink hover:text-ink-muted"
                                    >
                                        {c.cancel}
                                    </button>
                                    <button
                                        type="submit"
                                        data-testid="catalog-submit"
                                        disabled={state.loading}
                                        className="btn-primary disabled:opacity-50"
                                    >
                                        {state.loading ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                {c.submitting}
                                            </>
                                        ) : (
                                            <>
                                                <Send size={16} />
                                                {c.submit}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function CatalogDownload() {
    const { t } = useLang();
    const c = t.catalog;
    const [open, setOpen] = useState(false);
    const head = useReveal();

    return (
        <section
            id="catalog"
            data-testid="catalog-section"
            className="py-24 md:py-32 bg-ink text-white relative overflow-hidden"
        >
            <div className="absolute inset-0 hex-pattern opacity-25 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <div
                    ref={head.ref}
                    className={`${head.className} grid lg:grid-cols-12 gap-12 lg:gap-16 items-center`}
                >
                    {/* Left: visual */}
                    <div className="lg:col-span-5 relative">
                        <div className="absolute -inset-3 border border-mint hidden sm:block" />
                        <div className="absolute -top-4 -right-4 bg-mint text-ink p-3 hidden sm:flex items-center gap-2 font-display uppercase tracking-widest text-xs">
                            <BookOpen size={16} /> {c.badge}
                        </div>
                        <img
                            src={CATALOG_IMG}
                            alt={c.title}
                            className="relative w-full aspect-[4/5] object-cover grayscale"
                        />
                    </div>

                    {/* Right: copy + CTA */}
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-10 h-px bg-mint" />
                            <span className="font-display text-xs uppercase tracking-[0.3em] text-mint">
                                {c.kicker}
                            </span>
                        </div>
                        <h2 className="font-display font-bold uppercase text-3xl md:text-4xl lg:text-5xl leading-[1] tracking-tight text-white">
                            {c.title}
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
                            {c.subtitle}
                        </p>

                        <ul className="mt-8 space-y-3">
                            {c.highlights.map((h, i) => (
                                <li
                                    key={i}
                                    data-testid={`catalog-highlight-${i}`}
                                    className="flex items-start gap-3"
                                >
                                    <span className="w-6 h-6 bg-mint text-ink flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check size={14} />
                                    </span>
                                    <span className="text-white/90">{h}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-10">
                            <button
                                data-testid="catalog-cta"
                                onClick={() => setOpen(true)}
                                className="btn-mint group"
                            >
                                <FileDown size={16} />
                                {c.cta}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {open && <CatalogModal onClose={() => setOpen(false)} />}
        </section>
    );
}
